import { getAxiosClient } from '~src/api';
import { postEncryptedGiottoApiPayload } from '~core/aiBackground/promptEncryption';

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ENDPOINT = '/generate-image/background-fusion';
const BACKGROUND_FUSION_QUALITY = 'medium';
const DEFAULT_INITIAL_POLL_DELAY_MS = 100000;
const DEFAULT_POLL_INTERVAL_MS = 1000;
const DEFAULT_MAX_POLL_ATTEMPTS = 300;

const isPendingGenerationError = (error) => {
  const status = error?.response?.status;

  return (
    status === 404 ||
    status === 202 ||
    status === 425 ||
    status === 429 ||
    (status >= 500 && status < 600)
  );
};

const pollForBackgroundFusionResult = async ({
  httpClient,
  requestId,
  initialPollDelayMs,
  pollIntervalMs,
  maxAttempts,
}) => {
  if (initialPollDelayMs > 0) {
    await sleep(initialPollDelayMs);
  }

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      return await httpClient.get(
        `${process.env.GIOTTO_API_URL}${ENDPOINT}?requestId=${requestId}`
      );
    } catch (error) {
      if (!isPendingGenerationError(error) || attempt === maxAttempts) {
        throw error;
      }

      await sleep(pollIntervalMs);
    }
  }

  throw new Error('Background fusion polling exhausted');
};

/**
 * Genera un'immagine "background-fusion" tramite lambda-giotto-api
 * (modello fal.ai openai/gpt-image-2/edit).
 *
 * Endpoint asincrono: POST per accodare la richiesta, attesa iniziale per la
 * finestra in cui fal non risponde ancora, poi GET in polling fisso e
 * ravvicinato finché l'immagine è pronta.
 *
 * @param {Object} params
 * @param {string[]} params.imageUrls 2 URL pubbliche in ordine VINCOLANTE:
 *   [urlSfondo, urlPersona].
 * @param {number} params.width  larghezza in px. Vincoli fal: multiplo di 16,
 *   <= 3840, aspect ratio <= 3:1, 655360 <= width*height <= 8294400.
 * @param {number} params.height altezza in px (stessi vincoli).
 * @param {Object} [params.layout] geometria opzionale per preservare la
 *   posizione della foto nel canvas.
 * @param {string} [params.maskUrl] PNG mask opzionale per fal.ai:
 *   bianco = area editabile, nero = area da preservare.
 * @param {('jpeg'|'png'|'webp')} [params.outputFormat='png']
 * @param {number} [params.initialPollDelayMs=100000] attesa prima del polling.
 * @param {number} [params.pollIntervalMs=1000] intervallo fisso tra i poll.
 * @param {number} [params.maxAttempts=300] tentativi massimi dopo l'attesa.
 * @returns {Promise<string>} URL (CDN) dell'immagine generata.
 */
export default async function generateBackgroundFusion({
  imageUrls,
  width,
  height,
  layout,
  maskUrl,
  outputFormat = 'png',
  initialPollDelayMs = DEFAULT_INITIAL_POLL_DELAY_MS,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  maxAttempts = DEFAULT_MAX_POLL_ATTEMPTS,
}) {
  const payload = {
    imageUrls,
    quality: BACKGROUND_FUSION_QUALITY,
    outputFormat,
  };
  // Dimensions are optional: the backend infers them from the background
  // ('auto') unless an explicit canvas size is provided.
  if (width != null && height != null) {
    payload.width = width;
    payload.height = height;
  }
  if (layout) {
    payload.layout = layout;
  }
  if (maskUrl) {
    payload.maskUrl = maskUrl;
  }
  const httpClient = await getAxiosClient();

  const queueResponse = await postEncryptedGiottoApiPayload(
    httpClient,
    `${process.env.GIOTTO_API_URL}${ENDPOINT}`,
    payload
  );

  const { requestId } = queueResponse.data;

  const response = await pollForBackgroundFusionResult({
    httpClient,
    requestId,
    initialPollDelayMs,
    pollIntervalMs,
    maxAttempts,
  });

  return response.data.imageUrl;
}
